import axios from 'axios';
import {
  AdminTrafficActivitiesResponse,
  AdminTrafficInvalidation,
  AdminTrafficResponse,
  User
} from '../types';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get token from authStore
const getToken = () => {
  return useAuthStore.getState().token;
};

interface AdminAnalytics {
  overview: {
    totalUsers: number;
    usersWithInfo: number;
    usersWithoutInfo: number;
    completionRate: string;
  };
  demographics: {
    byGender: Array<{ _id: string; count: number }>;
    byStatsLevel: Array<{ _id: string; count: number }>;
    byCollege: Array<{ _id: string; count: number }>;
    byMajor: Array<{ _id: string; count: number }>;
    statsBackground: { yes: number; no: number };
  };
  recentUsers: User[];
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface RoleSummary {
  total: number;
  roles: Array<{ role: string; count: number }>;
}

export const getTraffic = async (
  page: number = 1,
  limit: number = 20,
  role: string = 'all',
  signal?: AbortSignal
): Promise<AdminTrafficResponse> => {
  const token = getToken();
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    role
  });
  const response = await axios.get(
    `${API_BASE_URL}/admin/traffic?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal
    }
  );
  return response.data.data;
};

export const getTrafficUserActivities = async (
  userId: string,
  limit: number = 50,
  cursor?: string,
  signal?: AbortSignal,
  category?: string,
  type?: string,
  background: boolean = false
): Promise<AdminTrafficActivitiesResponse> => {
  const token = getToken();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  if (category) params.set('category', category);
  if (type) params.set('type', type);
  if (background) params.set('background', '1');
  const response = await axios.get(
    `${API_BASE_URL}/admin/traffic/users/${encodeURIComponent(userId)}/activities?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal
    }
  );
  return response.data.data;
};

export class AdminTrafficStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AdminTrafficStreamError';
    this.status = status;
  }
}

export const streamTrafficInvalidations = async (
  signal: AbortSignal,
  onOpen: () => void,
  onInvalidation: (event: AdminTrafficInvalidation) => void
): Promise<void> => {
  const token = getToken();
  if (!token) throw new AdminTrafficStreamError('Authentication required', 401);
  const response = await fetch(`${API_BASE_URL}/admin/traffic/stream`, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`
    },
    cache: 'no-store',
    signal
  });
  if (!response.ok) {
    throw new AdminTrafficStreamError('Traffic stream unavailable', response.status);
  }
  if (!response.body) throw new AdminTrafficStreamError('Streaming is unsupported');
  onOpen();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const lines = frame.split('\n');
        const eventName = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
        const data = lines
          .filter(line => line.startsWith('data:'))
          .map(line => line.slice(5).trimStart())
          .join('\n');
        if (eventName === 'invalidation' && data) {
          try {
            const parsed = JSON.parse(data);
            if (
              typeof parsed?.activityId === 'string' &&
              typeof parsed?.type === 'string' &&
              typeof parsed?.affectedUserId === 'string' &&
              typeof parsed?.serverTime === 'string'
            ) {
              onInvalidation(parsed);
            }
          } catch {
            // A malformed frame terminates neither the authenticated stream nor
            // the current view; only fully allowlisted invalidations are used.
          }
        }
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
};

// Get all users with pagination, optionally scoped to a single role (e.g.
// 'student', 'teacher', 'content_manager') so the admin user management view
// can be organized by role instead of one flat list.
export const getAllUsers = async (page: number = 1, limit: number = 20, role?: string): Promise<UsersResponse> => {
  const token = getToken();
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (role && role !== 'all') params.append('role', role);
  const response = await axios.get(
    `${API_BASE_URL}/admin/users?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get the count of users per role, for role-organized tabs/badges.
export const getUserRoleSummary = async (): Promise<RoleSummary> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/admin/users/role-summary`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Get platform analytics
export const getAnalytics = async (): Promise<AdminAnalytics> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/admin/analytics`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Export user data
export const exportUserData = async (format: 'csv' | 'json' = 'json', anonymize: boolean = true): Promise<void> => {
  const token = getToken();
  const url = `${API_BASE_URL}/admin/export?format=${format}&anonymize=${anonymize}`;

  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: format === 'csv' ? 'blob' : 'json'
  });

  if (format === 'csv') {
    // Download CSV file
    const blob = new Blob([response.data], { type: 'text/csv' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `statquest-users-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } else {
    // Download JSON file
    const dataStr = JSON.stringify(response.data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `statquest-users-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
};

// Get user by ID
export const getUserById = async (userId: string): Promise<User> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/admin/users/${userId}`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Update user admin status
export const updateUserAdminStatus = async (userId: string, isAdmin: boolean): Promise<User> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/admin/users/${userId}/admin`,
    { isAdmin },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

// Delete user
export const deleteUser = async (userId: string, deleteAllData: boolean = false): Promise<void> => {
  const token = getToken();
  await axios.delete(
    `${API_BASE_URL}/admin/users/${userId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: deleteAllData ? { deleteAllData: 'true' } : undefined
    }
  );
};

// Teacher approval APIs
export const getPendingTeachers = async (): Promise<User[]> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/admin/teachers/pending`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const approveTeacher = async (teacherId: string): Promise<User> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/admin/teachers/${teacherId}/approve`,
    {},
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const rejectTeacher = async (teacherId: string, reason?: string): Promise<User> => {
  const token = getToken();
  const response = await axios.put(
    `${API_BASE_URL}/admin/teachers/${teacherId}/reject`,
    { reason },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};

export const getAllTeachers = async (): Promise<User[]> => {
  const token = getToken();
  const response = await axios.get(
    `${API_BASE_URL}/admin/teachers/all`,
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  return response.data.data;
};
